/* eslint-disable react/prop-types */
import React, { Component } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { pdf } from "@react-pdf/renderer";
import ApexCharts from "apexcharts";
import {
  Button, Grid, Form, Divider, Dropdown,
  Segment, Dimmer, Loader, Checkbox
} from "semantic-ui-react";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import H1Report from "./report/H1Report";
import Constants from "../utilities/Constants";
import Strings from "../utilities/Strings";
import Utils from "../utilities/Utils";

const UtilsInstance = new Utils();

const dropdownOptions = [
  {
    key: Constants.CONFORM,
    value: Constants.CONFORM,
    text: Strings.CONFORM
  },
  {
    key: Constants.CONFORM_W_RESTRICTION,
    value: Constants.CONFORM_W_RESTRICTION,
    text: Strings.CONFORM_W_RESTRICTION
  },
  {
    key: Constants.NON_CONFORM,
    value: Constants.NON_CONFORM,
    text: Strings.NON_CONFORM
  }
];

let h1WaveFormOptions = {
  series: [{ data: [] }],
  chart: {
    id: Constants.CHART_ID,
    type: "line",
  },
  colors: [Constants.RED_HEX],
  markers: {
    size: 0
  },
  dataLabels: {
    enabled: false
  },
  title: {
    text: Strings.WAVE_FORM,
    align: "center",
    style: {
      fontSize: "14px",
      fontFamily: "Helvetica, Arial, sans-serif"
    }
  },
  stroke: {
    curve: "straight",
    width: 2
  },
  xaxis: {
    type: "numeric",
    title: {
      text: Strings.TIME_US
    },
    crosshairs: {
      show: "true"
    },
    labels: {
      show: true,
      style: {
        fontSize: Constants.LABELS_FONT_SIZE,
        fontFamily: "Helvetica, Arial, sans-serif",
        cssClass: "apexcharts-xaxis-label",
      }
    }
  },
  yaxis: {
    min: -1000,
    max: 1000,
    labels: {
      show: true,
      formatter: function (val) {
        return val.toFixed(0) + " " + Strings.MILLIVOLT;
      },
      style: {
        colors: [],
        fontSize: Constants.LABELS_FONT_SIZE,
        fontFamily: "Helvetica, Arial, sans-serif",
        fontWeight: 400,
        cssClass: "apexcharts-yaxis-label",
      }
    },
    title: {
      text: Strings.VOLTAGE_MV
    },
  },
  annotations: {
    position: "front",
    xaxis: []
  }
};

let h1BarGraphOptions = {
  tooltip: {
    enabled: true,
    x: {
      show: true,
      formatter: function (val) {
        return Strings.ADDRESS + ": " + val;
      },
    },
  },
  chart: {
    id: Constants.AMPLITUDE_ID,
    type: "bar",
    locales: [{
      "name": "pt-br",
      "options": {
        "toolbar": {
          "exportToSVG": "Baixar SVG",
          "exportToPNG": "Baixar PNG",
          "exportToCSV": "Baixar CSV",
          "menu": "Baixar"
        }
      }
    }],
    defaultLocale: "pt-br",
  },
  title: {
    text: Strings.AMPLITUDE,
    align: "center"
  },
  xaxis: {
    type: "category",
    title: {
      text: Strings.ADDRESS
    },
    crosshairs: {
      show: "true"
    },
    labels: {
      show: true,
      style: {
        colors: [],
        fontSize: "16px",
        fontFamily: "Helvetica, Arial, sans-serif",
        fontWeight: 400,
        cssClass: "apexcharts-xaxis-label",
      }
    }
  },
  yaxis: {
    min: 0,
    max: 2200,
    labels: {
      show: true,
      formatter: function (val) {
        return val.toFixed(0) + " " + Strings.MILLIVOLT;
      },
      style: {
        colors: [],
        fontSize: "14px",
        fontFamily: "Helvetica, Arial, sans-serif",
        fontWeight: 400,
        cssClass: "apexcharts-yaxis-label",
      }
    },
    title: {
      text: Strings.VOLTAGE_MV
    },
  },
  series: [{
    name: Strings.AMPLITUDE,
    data: [],
  }],
  colors: [function ({ seriesIndex, dataPointIndex, w }) {
    const point = w.config.series[seriesIndex].data[dataPointIndex];
    if (point !== undefined){
      const status = point.status;
      const hasWaveProblem = point.hasWaveProblem;
      return (UtilsInstance.getH1BarGraphColorBasedOnValue(status, Constants, null, hasWaveProblem));
    }
  }],
  plotOptions: {
    bar: {
      dataLabels: {
        position: "top",
      }
    }
  },
  dataLabels: {
    formatter: function (val) {
      if (val < 0) {
        // Don't show the number above a bar with
        // negative values
        return "";
      }
      else if (val === 0) {
        return "0,00";
      }
      else {
        return val;
      }
    },
    style: {
      fontSize: "16px",
      fontFamily: "Helvetica, Arial, sans-serif",
      fontWeight: 400,
      colors: ["#212121"],
    },
    offsetY: -25,
  },
};

let h1NoiseBarGraphOptions = {
  tooltip: {
    enabled: true,
    x: {
      show: true,
      formatter: function (val) {
        return Strings.ADDRESS + ": " + val;
      },
    },
  },
  chart: {
    id: Constants.AMPLITUDE_ID,
    type: "bar",
    locales: [{
      "name": "pt-br",
      "options": {
        "toolbar": {
          "exportToSVG": "Baixar SVG",
          "exportToPNG": "Baixar PNG",
          "exportToCSV": "Baixar CSV",
          "menu": "Baixar"
        }
      }
    }],
    defaultLocale: "pt-br",
  },
  title: {
    text: Strings.NOISE,
    align: "center"
  },
  xaxis: {
    type: "category",
    title: {
      text: Strings.ADDRESS
    },
    crosshairs: {
      show: "true"
    },
    labels: {
      show: true,
      style: {
        colors: [],
        fontSize: "16px",
        fontFamily: "Helvetica, Arial, sans-serif",
        fontWeight: 400,
        cssClass: "apexcharts-xaxis-label",
      }
    }
  },
  yaxis: {
    min: 0,
    max: Constants.NOISE_MAX_Y_AXIS,
    labels: {
      formatter: function (val) {
        return val.toFixed(0) + " " + Strings.MILLIVOLT;
      },
    },
    title: {
      text: Strings.VOLTAGE_MV
    },
  },
  series: [{
    name: Strings.AMPLITUDE,
    data: [],
  }],
  colors: [function ({ seriesIndex, dataPointIndex, w }) {
    const point = w.config.series[seriesIndex].data[dataPointIndex];
    if (point !== undefined){
      const status = point.status;
      const hasWaveProblem = point.hasWaveProblem;
      return (UtilsInstance.getH1NoiseBarGraphColorBasedOnValue(status, Constants, null, hasWaveProblem));
    }
  }],
  plotOptions: {
    bar: {
      dataLabels: {
        position: "top",
      }
    }
  },
  dataLabels: {
    formatter: function (val) {
      if (val < 0) {
        // Don't show the number above a bar with
        // negative values
        return "";
      }
      else if (val === 0) {
        return "0,00";
      }
      else {
        return val;
      }
    },
    style: {
      fontSize: "16px",
      fontFamily: "Helvetica, Arial, sans-serif",
      fontWeight: 400,
      colors: ["#212121"],
    },
    offsetY: -25,
  },
};

let h1JitterBarGraphOptions = {
  tooltip: {
    enabled: true,
    x: {
      show: true,
      formatter: function (val) {
        return Strings.ADDRESS + ": " + val;
      },
    },
  },
  chart: {
    id: Constants.AMPLITUDE_ID,
    type: "bar",
    locales: [{
      "name": "pt-br",
      "options": {
        "toolbar": {
          "exportToSVG": "Baixar SVG",
          "exportToPNG": "Baixar PNG",
          "exportToCSV": "Baixar CSV",
          "menu": "Baixar"
        }
      }
    }],
    defaultLocale: "pt-br",
  },
  title: {
    text: Strings.JITTER,
    align: "center"
  },
  xaxis: {
    type: "category",
    title: {
      text: Strings.ADDRESS
    },
    crosshairs: {
      show: "true"
    },
    labels: {
      show: true,
      style: {
        colors: [],
        fontSize: "16px",
        fontFamily: "Helvetica, Arial, sans-serif",
        fontWeight: 400,
        cssClass: "apexcharts-xaxis-label",
      }
    }
  },
  yaxis: {
    min: 0,
    max: Constants.JITTER_MAX_Y_AXIS,
    labels: {
      formatter: function (val) {
        return val.toFixed(2) + " " + Strings.MICROSSECOND;
      },
    },
    title: {
      text: Strings.MICROSSECOND_TITLE
    },
  },
  series: [{
    name: Strings.AMPLITUDE,
    data: [],
  }],
  colors: [function ({ seriesIndex, dataPointIndex, w }) {
    const point = w.config.series[seriesIndex].data[dataPointIndex];
    if (point !== undefined){
      const status = point.status;
      const hasWaveProblem = point.hasWaveProblem;
      return (UtilsInstance.getH1JitterBarGraphColorBasedOnValue(status, Constants, null, hasWaveProblem));
    }
  }],
  plotOptions: {
    bar: {
      dataLabels: {
        position: "top",
      }
    }
  },
  dataLabels: {
    formatter: function (val) {
      if (val < 0) {
        // Don't show the number above a bar with
        // negative values
        return "";
      }
      else if (val === 0) {
        return "0,00";
      }
      else {
        return val;
      }
    },
    style: {
      fontSize: "16px",
      fontFamily: "Helvetica, Arial, sans-serif",
      fontWeight: 400,
      colors: ["#212121"],
    },
    offsetY: -25,
  },
};

let h1UnbalanceBarGraphOptions = {
  tooltip: {
    enabled: true,
    x: {
      show: true,
      formatter: function (val) {
        return Strings.ADDRESS + ": " + val;
      },
    },
  },
  chart: {
    id: Constants.AMPLITUDE_ID,
    type: "bar",
    locales: [{
      "name": "pt-br",
      "options": {
        "toolbar": {
          "exportToSVG": "Baixar SVG",
          "exportToPNG": "Baixar PNG",
          "exportToCSV": "Baixar CSV",
          "menu": "Baixar"
        }
      }
    }],
    defaultLocale: "pt-br",
  },
  title: {
    text: Strings.UNBALANCE,
    align: "center"
  },
  xaxis: {
    type: "category",
    title: {
      text: Strings.ADDRESS
    },
    crosshairs: {
      show: "true"
    },
    labels: {
      show: true,
      style: {
        colors: [],
        fontSize: "16px",
        fontFamily: "Helvetica, Arial, sans-serif",
        fontWeight: 400,
        cssClass: "apexcharts-xaxis-label",
      }
    }
  },
  yaxis: {
    min: 0,
    max: Constants.UNBALANCE_MAX_Y_AXIS,
    labels: {
      formatter: function (val) {
        return val.toFixed(2) + " " + Strings.PORCENTAGEM;
      },
    },
    title: {
      text: Strings.UNBALANCE_PORCENT
    },
  },
  series: [{
    name: Strings.AMPLITUDE,
    data: [],
  }],
  colors: [function ({ seriesIndex, dataPointIndex, w }) {
    const point = w.config.series[seriesIndex].data[dataPointIndex];
    if (point !== undefined){
      const status = point.status;
      const hasWaveProblem = point.hasWaveProblem;
      return (UtilsInstance.getH1UnbalanceBarGraphColorBasedOnValue(status, Constants, null, hasWaveProblem));
    }
  }],
  plotOptions: {
    bar: {
      dataLabels: {
        position: "top",
      }
    }
  },
  dataLabels: {
    formatter: function (val) {
      if (val < 0) {
        // Don't show the number above a bar with
        // negative values
        return "";
      }
      else if (val === 0) {
        return "0,00";
      }
      else {
        return val;
      }
    },
    style: {
      fontSize: "16px",
      fontFamily: "Helvetica, Arial, sans-serif",
      fontWeight: 400,
      colors: ["#212121"],
    },
    offsetY: -25,
  },
};

class Report extends Component {

  constructor(props) {
    super(props);

    this.state = {
      isGenerateReportDimmerActive: false,
      // User input data
      clientProjectName: "",
      clientNetworkName: "",
      clientLocation: "",
      clientCompanyName: "",
      clientAccountableName: "",
      clientAddress: "",
      clientCity: "",
      clientTel: "",
      executingCompanyName: "",
      executingAccountableName: "",
      executingTel: "",
      executingEmail: "",
      notes: "",
      conformity: "",

      report: "",

      // Channel Checkboxes
      isChannel1Checked: false,
      isChannel2Checked: false,
      isChannel3Checked: false,
      isChannel4Checked: false
    };
  }

  setGeneratingDimmerState = (isActive) => {
    this.setState({ isGenerateReportDimmerActive: isActive });
  }

  // Since the devices in H1 protocol are not numbered with simple integers
  // We need to convert, 1A and 1B for exemple, into sortable integers
  // Every device that has A in its address has its number as the sortable integer
  // Every device that has B in its addres, has its number + 32 as the sortable integer
  // Every device that has master in its name has its number be 0
  addOrderValueToDevices = (devices) => {
    let devicesCopy = devices;

    for (let i = 0; i < devicesCopy.length; i++) {
      let deviceName = devicesCopy[i].address;
      devicesCopy[i].order = parseInt(deviceName);
    }

    return (
      devicesCopy.sort(UtilsInstance.sortBy(
        "order",
        false,
        parseInt))
    );
  }

  addOrderValueToOscilloscopeData = (oscilloscopeData) => {
    let oscilloscopeDataCopy = oscilloscopeData;

    for (let i = 0; i < oscilloscopeDataCopy.length; i++) {
      let deviceName = oscilloscopeDataCopy[i].address;
      oscilloscopeDataCopy[i].order = parseInt(deviceName);
    }

    return (
      oscilloscopeDataCopy.sort(UtilsInstance.sortBy(
        "order",
        false,
        parseInt))
    );
  }

  getFormatedH1Options = (wave) => {
    let options = {};
    // Set the time values to microsseconds
    options = {
      ...this.state.options,
      xaxis: {
        type: "numeric",
        title: {
          text: Strings.TIME_US
        },
        crosshairs: {
          show: "true"
        }
      }
    };

    return options;
  }

  formatWaveTimes = (wave) => {
    // Set the time values to microsseconds
    for (let i = 0; i < wave.length; i++) {
      wave[i][0] = wave[i][0] / (10 ** 3);
    }

    return wave;
  }

  getNewest200Events = (log) => {
    let newest200Events = [];

    if (log.length > Constants.MAX_NUM_OF_LOG_EVENTS) {
      for (let i = 0; i < Constants.MAX_NUM_OF_LOG_EVENTS; i++) {
        newest200Events.push(log[i]);
      }
    }
    else {
      for (let i = 0; i < log.length; i++) {
        newest200Events.push(log[i]);
      }
    }

    return newest200Events;
  }

  convertAmplitudeValuesTomV = (data) => {
    for (let i = 0; i < data.length; i++) {
      data[i].amplitude = data[i].amplitude * 1000;
      data[i].amplitudeMin = data[i].amplitudeMin * 1000;
      data[i].noise = data[i].noise * 1000;
    }

    return data;
  }

  retrieveAnalyzerH1Data = async (userInput) => {
    this.setGeneratingDimmerState(true);

    let channels = [];
    let liveListList = [];
    let livelistMapList = [];
    let networkDataList = [];
    let oscilloscopeDataList = [];
    let imgURIsList = [];
    let ampImgURIsList = [];
    let noiseImgURIsList = [];
    let jitterImgURIsList = [];
    let unbalanceImgURIsList = [];
    let logList = [];

    let zip = new JSZip();

    let homePage = await fetch(Constants.REQUEST_IP_ADDRESS + "homePage", { mode: "cors" })
      .then(res => res.json())
      .then(data => data);

    let configData = await fetch(Constants.REQUEST_IP_ADDRESS + "config", { mode: "cors" })
      .then(res => res.json())
      .then(data => data);
    const reportHost = configData.addr || Constants.REQUEST_HOSTNAME;

    let wifiStatus = await fetch(Constants.REQUEST_IP_ADDRESS + "wifiStatus", { mode: "cors" })
      .then(res => res.json())
      .then(data => data.status);

    let wifiConfigData = "";

    if (wifiStatus === Constants.CONNECTED_STATUS) {
      wifiConfigData = await fetch(Constants.REQUEST_IP_ADDRESS + "wifiConfig", { mode: "cors" })
        .then(res => res.json())
        .then(data => data);
    }
    else {
      wifiConfigData = {
        "wifiMode": "",
        "hiddenSSID": false,
        "wifiName": "",
        "wifiAddr": "",
        "wifiNetmask": "",
        "wifiGateway": "",
        "wifiDNS": "",
        "wifiPwd": ""
      };
    }

    if (this.state.isChannel1Checked) {
      channels = [];
      liveListList = [];
      livelistMapList = [];
      networkDataList = [];
      oscilloscopeDataList = [];
      imgURIsList = [];
      ampImgURIsList = [];
      noiseImgURIsList = [];
      jitterImgURIsList = [];
      unbalanceImgURIsList  = [];
      logList = [];

      let liveList = await fetch(Constants.REQUEST_IP_ADDRESS + "liveList/1", { mode: "cors" })
        .then(res => res.json())
        .then(data => this.convertAmplitudeValuesTomV(this.addOrderValueToDevices(data)));

      let livelistMap = this.createLivelistMap(liveList);

      let oscilloscopeData = await fetch(Constants.REQUEST_IP_ADDRESS + "oscilloscopeWaveAll/1", { mode: "cors" })
        .then(res => res.json())
        .then(data => this.addOrderValueToOscilloscopeData(data));

      let networkData = await fetch(Constants.REQUEST_IP_ADDRESS + "networkData/1", { mode: "cors" })
        .then(res => res.json())
        .then(data => data);

      let logData = await fetch(Constants.REQUEST_IP_ADDRESS + "log/1", { mode: "cors" })
        .then(res => res.json())
        .then(data => this.getNewest200Events(data));

      let channelTag = await fetch(Constants.REQUEST_IP_ADDRESS + "getChannelTagsAndNotes/1", { mode: "cors", })
        .then(res => res.json())
        .then(data => data.tag);

      let imgURIs = await this.getH1OscilloscopeImgURIs(oscilloscopeData);
      let ampImgURIs = await this.getH1BarGraphImgURIsAmp(liveList);
      let noiseImgURIs = await this.getH1BarGraphImgURIsNoise(liveList);
      let jitterImgURIs = await this.getH1BarGraphImgURIsJitter(liveList);
      let unbalanceImgURIs = await this.getH1BarGraphImgURIsUnbalance(liveList);

      channels.push(1);
      liveListList.push(liveList);
      livelistMapList.push(livelistMap);
      oscilloscopeDataList.push(oscilloscopeData);
      networkDataList.push(networkData);
      imgURIsList.push(imgURIs);
      ampImgURIsList.push(ampImgURIs);
      noiseImgURIsList.push(noiseImgURIs);
      jitterImgURIsList.push(jitterImgURIs);
      unbalanceImgURIsList.push(unbalanceImgURIs);
      logList.push(logData);

      let report = await pdf(<H1Report
        channels={channels}
        userInput={userInput}
        livelist={liveListList}
        livelistMap={livelistMapList}
        networkData={networkDataList}
        imgURIs={imgURIsList}
        ampImgURIs={ampImgURIsList}
        noiseImgURIs={noiseImgURIsList}
        jitterImgURIs={jitterImgURIsList}
        unbalanceImgURIs={unbalanceImgURIsList}
        log={logList}
        channelTag={channelTag}
        scopeTag={homePage.networkTag}
        uptime={homePage.uptime}
        configData={configData}
        wifiConfigData={wifiConfigData}
        conformity={this.state.conformity}
      />).toBlob();

      zip.file(`Relatório TS Scope H1 - Canal ${1} (${reportHost}).pdf`, report);
    }

    if (this.state.isChannel2Checked) {
      channels = [];
      liveListList = [];
      livelistMapList = [];
      networkDataList = [];
      oscilloscopeDataList = [];
      imgURIsList = [];
      ampImgURIsList = [];
      noiseImgURIsList = [];
      jitterImgURIsList = [];
      unbalanceImgURIsList  = [];
      logList = [];

      let liveList = await fetch(Constants.REQUEST_IP_ADDRESS + "liveList/2", { mode: "cors" })
        .then(res => res.json())
        .then(data => this.convertAmplitudeValuesTomV(this.addOrderValueToDevices(data)));

      let livelistMap = this.createLivelistMap(liveList);

      let oscilloscopeData = await fetch(Constants.REQUEST_IP_ADDRESS + "oscilloscopeWaveAll/2", { mode: "cors" })
        .then(res => res.json())
        .then(data => this.addOrderValueToOscilloscopeData(data));

      let networkData = await fetch(Constants.REQUEST_IP_ADDRESS + "networkData/2", { mode: "cors" })
        .then(res => res.json())
        .then(data => data);

      let logData = await fetch(Constants.REQUEST_IP_ADDRESS + "log/2", { mode: "cors" })
        .then(res => res.json())
        .then(data => this.getNewest200Events(data));

      let channelTag = await fetch(Constants.REQUEST_IP_ADDRESS + "getChannelTagsAndNotes/2", { mode: "cors", })
        .then(res => res.json())
        .then(data => data.tag);

      let imgURIs = await this.getH1OscilloscopeImgURIs(oscilloscopeData);
      let ampImgURIs = await this.getH1BarGraphImgURIsAmp(liveList);
      let noiseImgURIs = await this.getH1BarGraphImgURIsNoise(liveList);
      let jitterImgURIs = await this.getH1BarGraphImgURIsJitter(liveList);
      let unbalanceImgURIs = await this.getH1BarGraphImgURIsUnbalance(liveList);

      channels.push(2);
      liveListList.push(liveList);
      livelistMapList.push(livelistMap);
      oscilloscopeDataList.push(oscilloscopeData);
      networkDataList.push(networkData);
      imgURIsList.push(imgURIs);
      ampImgURIsList.push(ampImgURIs);
      noiseImgURIsList.push(noiseImgURIs);
      jitterImgURIsList.push(jitterImgURIs);
      unbalanceImgURIsList.push(unbalanceImgURIs);
      logList.push(logData);

      let report = await pdf(<H1Report
        channels={channels}
        userInput={userInput}
        livelist={liveListList}
        livelistMap={livelistMapList}
        networkData={networkDataList}
        imgURIs={imgURIsList}
        ampImgURIs={ampImgURIsList}
        noiseImgURIs={noiseImgURIsList}
        jitterImgURIs={jitterImgURIsList}
        unbalanceImgURIs={unbalanceImgURIsList}
        log={logList}
        channelTag={channelTag}
        scopeTag={homePage.networkTag}
        uptime={homePage.uptime}
        configData={configData}
        wifiConfigData={wifiConfigData}
        conformity={this.state.conformity}
      />).toBlob();

      zip.file(`Relatório TS Scope H1 - Canal ${2} (${reportHost}).pdf`, report);
    }

    if (this.state.isChannel3Checked) {
      channels = [];
      liveListList = [];
      livelistMapList = [];
      networkDataList = [];
      oscilloscopeDataList = [];
      imgURIsList = [];
      ampImgURIsList = [];
      noiseImgURIsList = [];
      jitterImgURIsList = [];
      unbalanceImgURIsList  = [];
      logList = [];

      let liveList = await fetch(Constants.REQUEST_IP_ADDRESS + "liveList/3", { mode: "cors" })
        .then(res => res.json())
        .then(data => this.convertAmplitudeValuesTomV(this.addOrderValueToDevices(data)));

      let livelistMap = this.createLivelistMap(liveList);

      let oscilloscopeData = await fetch(Constants.REQUEST_IP_ADDRESS + "oscilloscopeWaveAll/3", { mode: "cors" })
        .then(res => res.json())
        .then(data => this.addOrderValueToOscilloscopeData(data));

      let networkData = await fetch(Constants.REQUEST_IP_ADDRESS + "networkData/3", { mode: "cors" })
        .then(res => res.json())
        .then(data => data);

      let logData = await fetch(Constants.REQUEST_IP_ADDRESS + "log/3", { mode: "cors" })
        .then(res => res.json())
        .then(data => this.getNewest200Events(data));

      let channelTag = await fetch(Constants.REQUEST_IP_ADDRESS + "getChannelTagsAndNotes/3", { mode: "cors", })
        .then(res => res.json())
        .then(data => data.tag);

      let imgURIs = await this.getH1OscilloscopeImgURIs(oscilloscopeData);
      let ampImgURIs = await this.getH1BarGraphImgURIsAmp(liveList);
      let noiseImgURIs = await this.getH1BarGraphImgURIsNoise(liveList);
      let jitterImgURIs = await this.getH1BarGraphImgURIsJitter(liveList);
      let unbalanceImgURIs = await this.getH1BarGraphImgURIsUnbalance(liveList);

      channels.push(3);
      liveListList.push(liveList);
      livelistMapList.push(livelistMap);
      oscilloscopeDataList.push(oscilloscopeData);
      networkDataList.push(networkData);
      imgURIsList.push(imgURIs);
      ampImgURIsList.push(ampImgURIs);
      noiseImgURIsList.push(noiseImgURIs);
      jitterImgURIsList.push(jitterImgURIs);
      unbalanceImgURIsList.push(unbalanceImgURIs);
      logList.push(logData);

      let report = await pdf(<H1Report
        channels={channels}
        userInput={userInput}
        livelist={liveListList}
        livelistMap={livelistMapList}
        networkData={networkDataList}
        imgURIs={imgURIsList}
        ampImgURIs={ampImgURIsList}
        noiseImgURIs={noiseImgURIsList}
        jitterImgURIs={jitterImgURIsList}
        unbalanceImgURIs={unbalanceImgURIsList}
        log={logList}
        channelTag={channelTag}
        scopeTag={homePage.networkTag}
        uptime={homePage.uptime}
        configData={configData}
        wifiConfigData={wifiConfigData}
        conformity={this.state.conformity}
      />).toBlob();

      zip.file(`Relatório TS Scope H1 - Canal ${3} (${reportHost}).pdf`, report);
    }

    if (this.state.isChannel4Checked) {
      channels = [];
      liveListList = [];
      livelistMapList = [];
      networkDataList = [];
      oscilloscopeDataList = [];
      imgURIsList = [];
      ampImgURIsList = [];
      noiseImgURIsList = [];
      jitterImgURIsList = [];
      unbalanceImgURIsList = [];
      logList = [];

      let liveList = await fetch(Constants.REQUEST_IP_ADDRESS + "liveList/4", { mode: "cors" })
        .then(res => res.json())
        .then(data => this.convertAmplitudeValuesTomV(this.addOrderValueToDevices(data)));

      let livelistMap = this.createLivelistMap(liveList);

      let oscilloscopeData = await fetch(Constants.REQUEST_IP_ADDRESS + "oscilloscopeWaveAll/4", { mode: "cors" })
        .then(res => res.json())
        .then(data => this.addOrderValueToOscilloscopeData(data));

      let networkData = await fetch(Constants.REQUEST_IP_ADDRESS + "networkData/4", { mode: "cors" })
        .then(res => res.json())
        .then(data => data);

      let logData = await fetch(Constants.REQUEST_IP_ADDRESS + "log/4", { mode: "cors" })
        .then(res => res.json())
        .then(data => this.getNewest200Events(data));

      let channelTag = await fetch(Constants.REQUEST_IP_ADDRESS + "getChannelTagsAndNotes/4", { mode: "cors", })
        .then(res => res.json())
        .then(data => data.tag);

      let imgURIs = await this.getH1OscilloscopeImgURIs(oscilloscopeData);
      let ampImgURIs = await this.getH1BarGraphImgURIsAmp(liveList);
      let noiseImgURIs = await this.getH1BarGraphImgURIsNoise(liveList);
      let jitterImgURIs = await this.getH1BarGraphImgURIsJitter(liveList);
      let unbalanceImgURIs = await this.getH1BarGraphImgURIsUnbalance(liveList);

      channels.push(4);
      liveListList.push(liveList);
      livelistMapList.push(livelistMap);
      oscilloscopeDataList.push(oscilloscopeData);
      networkDataList.push(networkData);
      imgURIsList.push(imgURIs);
      ampImgURIsList.push(ampImgURIs);
      noiseImgURIsList.push(noiseImgURIs);
      jitterImgURIsList.push(jitterImgURIs);
      unbalanceImgURIsList.push(unbalanceImgURIs);
      logList.push(logData);

      let report = await pdf(<H1Report
        channels={channels}
        userInput={userInput}
        livelist={liveListList}
        livelistMap={livelistMapList}
        networkData={networkDataList}
        imgURIs={imgURIsList}
        ampImgURIs={ampImgURIsList}
        noiseImgURIs={noiseImgURIsList}
        jitterImgURIs={jitterImgURIsList}
        unbalanceImgURIs={unbalanceImgURIsList}
        log={logList}
        channelTag={channelTag}
        scopeTag={homePage.networkTag}
        uptime={homePage.uptime}
        configData={configData}
        wifiConfigData={wifiConfigData}
        conformity={this.state.conformity}
      />).toBlob();

      zip.file(`Relatório TS Scope H1 - Canal ${4} (${reportHost}).pdf`, report);
    }

    zip.generateAsync({ type: "blob" })
      .then((content) => {
        // eslint-disable-next-line no-undef
        saveAs(content, "Relatórios - TS Scope H1.zip");
        this.setState({ isGenerateReportDimmerActive: false });
      });
  }

  createLivelistMap = (livelistData) => {
    let livelistMap = new Map();

    for (let i = 0; i < livelistData.length; i++) {
      livelistMap.set(livelistData[i].address, livelistData[i]);
    }

    return livelistMap;
  }

  convertWaveValuesTomV = (wave) => {
    for (let i = 0; i < wave.length; i++) {
      wave[i][1] = wave[i][1] * 1000;
    }

    return wave;
  }


  getH1OscilloscopeImgURIs = async (oscilloscopeData) => {
    let imgURIs = [];
    document.getElementById("chart").style.display = "block";

    // Render the chart
    let chart = new ApexCharts(document.querySelector("#chart"), h1WaveFormOptions);
    chart.render();

    for (let i = 0; i < oscilloscopeData.length; i++) {

      let imgURIObj = { "address": -1, "wave": "" };
      let options = {};

      options = this.getFormatedH1Options(oscilloscopeData[i].wave);

      options = {
        ...options,
        colors: [Constants.RED_HEX],
        series: [
          {
            name: Strings.WAVE_H1,
            data: this.formatWaveTimes(oscilloscopeData[i].wave)
          }
        ]
      };

      chart.updateOptions(options, false, false, false);

      imgURIObj.wave = await chart.dataURI();

      imgURIObj.address = await oscilloscopeData[i].address;

      // Save the wave URI
      imgURIs.push(imgURIObj);
    }

    document.getElementById("chart").style.display = "none";

    chart.destroy();

    return imgURIs;
  }

  getH1BarGraphImgURIsAmp = async (livelist) => {
    let ampImgURIs = [];

    document.getElementById("barchart").style.display = "block";
    // Render the chart
    let apexChart = new ApexCharts(document.querySelector("#barchart"), h1BarGraphOptions);
    apexChart.render();

    let numOfPages = Math.ceil(livelist.length / Constants.MAX_DEV_PER_PAGE);

    let ampMap = this.createAscAddrAmpPageMap(numOfPages, livelist);

    let imgURIObj = {};

    for (let i = 0; i < numOfPages; i++) {
      let options = {};

      // ----------- AMPLITUDE -----------
      options = {
        ...h1BarGraphOptions,
        series: [
          { name: Strings.AMPLITUDE, data: ampMap.get(i) }
        ]
      };

      apexChart.updateOptions(options, false, false, false);

      imgURIObj = await apexChart.dataURI();

      ampImgURIs.push(imgURIObj);
    }

    document.getElementById("barchart").style.display = "none";

    apexChart.destroy();

    return ampImgURIs;
  }

  getH1BarGraphImgURIsNoise = async (livelist) => {
    let noiseImgURIs = [];

    document.getElementById("barchart").style.display = "block";
    // Render the chart
    let apexChart = new ApexCharts(document.querySelector("#barchart"), h1NoiseBarGraphOptions);
    apexChart.render();

    let numOfPages = Math.ceil(livelist.length / Constants.MAX_DEV_PER_PAGE);

    let noiseMap = this.createAscAddrNoisePageMap(numOfPages, livelist);

    let imgURIObj = {};

    for (let i = 0; i < numOfPages; i++) {
      let options = {};

      // ----------- NOISE -----------
      options = {
        ...h1NoiseBarGraphOptions,
        series: [
          { name: Strings.NOISE, data: noiseMap.get(i) }
        ]
      };

      apexChart.updateOptions(options, false, false, false);

      imgURIObj = await apexChart.dataURI();

      noiseImgURIs.push(imgURIObj);
    }

    document.getElementById("barchart").style.display = "none";

    apexChart.destroy();

    return noiseImgURIs;
  }

  getH1BarGraphImgURIsJitter = async (livelist) => {
    let jitterImgURIs = [];

    document.getElementById("barchart").style.display = "block";
    // Render the chart
    let apexChart = new ApexCharts(document.querySelector("#barchart"), h1JitterBarGraphOptions);
    apexChart.render();

    let numOfPages = Math.ceil(livelist.length / Constants.MAX_DEV_PER_PAGE);

    let devMap = this.createAscAddrJitterPageMap(numOfPages, livelist);

    let imgURIObj = {};

    for (let i = 0; i < numOfPages; i++) {
      let options = {};

      // ----------- DEVIATION -----------
      options = {
        ...h1JitterBarGraphOptions,
        series: [
          { name: Strings.JITTER, data: devMap.get(i) }
        ]
      };

      apexChart.updateOptions(options, false, false, false);

      imgURIObj = await apexChart.dataURI();

      jitterImgURIs.push(imgURIObj);

    }

    document.getElementById("barchart").style.display = "none";

    apexChart.destroy();

    return jitterImgURIs;
  }

  getH1BarGraphImgURIsUnbalance= async (livelist) => {
    let unbalanceImgURIs = [];

    document.getElementById("barchart").style.display = "block";
    // Render the chart
    let apexChart = new ApexCharts(document.querySelector("#barchart"), h1UnbalanceBarGraphOptions);
    apexChart.render();

    let numOfPages = Math.ceil(livelist.length / Constants.MAX_DEV_PER_PAGE);

    let devMap = this.createAscAddrUnbalancePageMap(numOfPages, livelist);

    let imgURIObj = {};

    for (let i = 0; i < numOfPages; i++) {
      let options = {};

      // ----------- DEVIATION -----------
      options = {
        ...h1UnbalanceBarGraphOptions,
        series: [
          { name: Strings.UNBALANCE, data: devMap.get(i) }
        ]
      };

      apexChart.updateOptions(options, false, false, false);

      imgURIObj = await apexChart.dataURI();

      unbalanceImgURIs.push(imgURIObj);

    }

    document.getElementById("barchart").style.display = "none";

    apexChart.destroy();

    return unbalanceImgURIs;
  }

  // Amplitude
  createAscAddrAmpPageMap = (numOfPages, livelist) => {
    let ascAddrAmpMap = new Map();

    let ascAddrSortedDevices = livelist.sort(
      UtilsInstance.sortBy(
        "order",
        false,
        parseInt)
    );

    for (let i = 0; i < numOfPages; i++) {
      let devicesSlice = [];

      if (i === (numOfPages - 1)) {
        devicesSlice = ascAddrSortedDevices.slice(i * Constants.MAX_DEV_PER_PAGE, ascAddrSortedDevices.length);
      }
      else {
        devicesSlice = ascAddrSortedDevices.slice(i * Constants.MAX_DEV_PER_PAGE, (i + 1) * Constants.MAX_DEV_PER_PAGE);
      }

      let ampPageSeries = [];

      for (let j = 0; j < devicesSlice.length; j++) {
        ampPageSeries.push({
          x: devicesSlice[j].address.toString() + UtilsInstance.getMasterSuffix(devicesSlice[j].isMaster),
          y: UtilsInstance.getH1FormatedAmplitudeForFaded(
            parseFloat(devicesSlice[j].amplitude.toFixed(0)),
            devicesSlice[j].hasWaveProblem
          ),
          status: devicesSlice[j].status,
          hasWaveProblem: devicesSlice[j].hasWaveProblem
        });
      }

      // Complete the last slice with fake data, until it has exactly 15
      // bars to show on the screen
      if (i === (numOfPages - 1)) {
        let remainingDevices = Constants.MAX_DEV_PER_PAGE - ampPageSeries.length;

        if (remainingDevices > 0) {
          for (let j = 0; j < remainingDevices; j++) {
            ampPageSeries.push({ x: "", y: -1 });
          }
        }

      }

      ascAddrAmpMap.set(i, ampPageSeries);
    }

    return ascAddrAmpMap;
  }

  // Noise
  createAscAddrNoisePageMap = (numOfPages, livelist) => {
    let ascAddrNoiseMap = new Map();

    let ascAddrSortedDevices = livelist.sort(
      UtilsInstance.sortBy(
        "order",
        false,
        parseInt)
    );

    for (let i = 0; i < numOfPages; i++) {
      let devicesSlice = [];

      if (i === (numOfPages - 1)) {
        devicesSlice = ascAddrSortedDevices.slice(i * Constants.MAX_DEV_PER_PAGE, ascAddrSortedDevices.length);
      }
      else {
        devicesSlice = ascAddrSortedDevices.slice(i * Constants.MAX_DEV_PER_PAGE, (i + 1) * Constants.MAX_DEV_PER_PAGE);
      }

      let noisePageSeries = [];

      for (let j = 0; j < devicesSlice.length; j++) {
        noisePageSeries.push({
          x: devicesSlice[j].address.toString() + UtilsInstance.getMasterSuffix(devicesSlice[j].isMaster),
          y: UtilsInstance.getH1FormatedNoiseForFaded(
            parseFloat(devicesSlice[j].noise.toFixed(0)),
            devicesSlice[j].hasWaveProblem
          ),
          status: devicesSlice[j].statusNoise,
          hasWaveProblem: devicesSlice[j].hasWaveProblem
        });
      }

      // Complete the last slice with fake data, until it has exactly 15
      // bars to show on the screen
      if (i === (numOfPages - 1)) {
        let remainingDevices = Constants.MAX_DEV_PER_PAGE - noisePageSeries.length;

        if (remainingDevices > 0) {
          for (let j = 0; j < remainingDevices; j++) {
            noisePageSeries.push({ x: "", y: -1 });
          }
        }

      }

      ascAddrNoiseMap.set(i, noisePageSeries);
    }

    return ascAddrNoiseMap;
  }

  // Deviation
  createAscAddrJitterPageMap = (numOfPages, livelist) => {
    let ascAddrJitterMap = new Map();

    let ascAddrSortedDevices = livelist.sort(
      UtilsInstance.sortBy(
        "order",
        false,
        parseInt)
    );

    for (let i = 0; i < numOfPages; i++) {
      let devicesSlice = [];

      if (i === (numOfPages - 1)) {
        devicesSlice = ascAddrSortedDevices.slice(i * Constants.MAX_DEV_PER_PAGE, ascAddrSortedDevices.length);
      }
      else {
        devicesSlice = ascAddrSortedDevices.slice(i * Constants.MAX_DEV_PER_PAGE, (i + 1) * Constants.MAX_DEV_PER_PAGE);
      }

      let jitterPageSeries = [];

      for (let j = 0; j < devicesSlice.length; j++) {
        jitterPageSeries.push({
          x: devicesSlice[j].address.toString() + UtilsInstance.getMasterSuffix(devicesSlice[j].isMaster),
          y: UtilsInstance.getH1FormatedJitterForFaded(
            parseFloat(devicesSlice[j].jitter.toFixed(0)),
            devicesSlice[j].hasWaveProblem
          ),
          status: devicesSlice[j].statusJitter,
          hasWaveProblem: devicesSlice[j].hasWaveProblem
        });
      }

      // Complete the last slice with fake data, until it has exactly 15
      // bars to show on the screen
      if (i === (numOfPages - 1)) {
        let remainingDevices = Constants.MAX_DEV_PER_PAGE - jitterPageSeries.length;

        if (remainingDevices > 0) {
          for (let j = 0; j < remainingDevices; j++) {
            jitterPageSeries.push({ x: "", y: -1 });
          }
        }

      }

      ascAddrJitterMap.set(i, jitterPageSeries);
    }

    return ascAddrJitterMap;
  }

  createAscAddrUnbalancePageMap = (numOfPages, livelist) => {
    let ascAddrUnbalanceMap = new Map();

    let ascAddrSortedDevices = livelist.sort(
      UtilsInstance.sortBy(
        "order",
        false,
        parseInt)
    );

    for (let i = 0; i < numOfPages; i++) {
      let devicesSlice = [];

      if (i === (numOfPages - 1)) {
        devicesSlice = ascAddrSortedDevices.slice(i * Constants.MAX_DEV_PER_PAGE, ascAddrSortedDevices.length);
      }
      else {
        devicesSlice = ascAddrSortedDevices.slice(i * Constants.MAX_DEV_PER_PAGE, (i + 1) * Constants.MAX_DEV_PER_PAGE);
      }

      let unbalancePageSeries = [];

      for (let j = 0; j < devicesSlice.length; j++) {
        unbalancePageSeries.push({
          x: devicesSlice[j].address.toString() + UtilsInstance.getMasterSuffix(devicesSlice[j].isMaster),
          y: UtilsInstance.getH1FormatedUnbalanceForFaded(
            parseFloat((devicesSlice[j].absUnbalance*100).toFixed(2)),
            devicesSlice[j].hasWaveProblem
          ),
          status: devicesSlice[j].statusUnbalance,
          hasWaveProblem: devicesSlice[j].hasWaveProblem
        });
      }

      // Complete the last slice with fake data, until it has exactly 15
      // bars to show on the screen
      if (i === (numOfPages - 1)) {
        let remainingDevices = Constants.MAX_DEV_PER_PAGE - unbalancePageSeries.length;

        if (remainingDevices > 0) {
          for (let j = 0; j < remainingDevices; j++) {
            unbalancePageSeries.push({ x: "", y: -1 });
          }
        }

      }

      ascAddrUnbalanceMap.set(i, unbalancePageSeries);
    }

    return ascAddrUnbalanceMap;
  }

  // ----------------- Event Handlers -----------------
  handleChange = (e, { name, value }) => this.setState({ [name]: value })

  handleConformityDropdownItemClick = (event, info) => {
    this.setState({ conformity: info.value });
  }

  handleSubmit = () => {
    // If none of the checkboxes are selected
    if (!this.state.isChannel1Checked
      && !this.state.isChannel2Checked
      && !this.state.isChannel3Checked
      && !this.state.isChannel4Checked) {
      alert(Strings.SELECT_REPORT_TYPE);
    }
    else {

      let clientImg = "";
      let executorImg = "";

      if (document.getElementById("client-img").files[0] !== undefined) {
        clientImg = URL.createObjectURL(document.getElementById("client-img").files[0]);
      }

      if (document.getElementById("executor-img").files[0] !== undefined) {
        executorImg = URL.createObjectURL(document.getElementById("executor-img").files[0]);
      }

      document.getElementById("chart").style.display = "block";
      document.getElementById("barchart").style.display = "block";

      this.setState({ report: "" }, () => {
        let userInput = {
          // Client info
          clientProjectName: this.state.clientProjectName,
          clientNetworkName: this.state.clientNetworkName,
          clientLocation: this.state.clientLocation,
          clientCompanyName: this.state.clientCompanyName,
          clientAccountableName: this.state.clientAccountableName,
          clientAddress: this.state.clientAddress,
          clientCity: this.state.clientCity,
          clientTel: this.state.clientTel,
          clientImg: clientImg,
          // Executing company info
          executingCompanyName: this.state.executingCompanyName,
          executingAccountableName: this.state.executingAccountableName,
          executingTel: this.state.executingTel,
          executingEmail: this.state.executingEmail,
          executorImg: executorImg,
          // Notes
          notes: this.state.notes,
          // Conformity
          conformity: this.state.conformity
        };
        this.retrieveAnalyzerH1Data(userInput);
      });
    }
  }

  toggleChannel1Checkbox = () => {
    this.setState({ isChannel1Checked: !this.state.isChannel1Checked });
  }

  toggleChannel2Checkbox = () => {
    this.setState({ isChannel2Checked: !this.state.isChannel2Checked });
  }

  toggleChannel3Checkbox = () => {
    this.setState({ isChannel3Checked: !this.state.isChannel3Checked });
  }

  toggleChannel4Checkbox = () => {
    this.setState({ isChannel4Checked: !this.state.isChannel4Checked });
  }

  isUserLoggedIn = () => {
    if (sessionStorage.auth !== Constants.AUTH_STR) {
      this.props.history.push({ pathname: "/login" });
    }
  }

  // ----------------- Lifecycle Methods ----------------
  componentDidMount() {
    this.isUserLoggedIn();
  }

  render() {
    const {
      clientProjectName,
      clientNetworkName,
      clientLocation,
      clientCompanyName,
      clientAccountableName,
      clientAddress,
      clientCity,
      clientTel,
      executingCompanyName,
      executingAccountableName,
      executingTel,
      executingEmail,
      notes
    } = this.state;

    return (
      <div style={divStyle}>

        <Grid columns={2} doubling stretched>
          <Grid.Column>
            <Segment>
              <Divider horizontal>{Strings.CLIENT_INFO}</Divider>
              <Form>
                <Form.Input label="Nome do projeto" name='clientProjectName' value={clientProjectName} onChange={this.handleChange} />
                <Form.Input label="Nome da rede" name='clientNetworkName' value={clientNetworkName} onChange={this.handleChange} />
                <Form.Input label="Localização" name='clientLocation' value={clientLocation} onChange={this.handleChange} />
                <Form.Input label="Nome da empresa" name='clientCompanyName' value={clientCompanyName} onChange={this.handleChange} />
                <Form.Input label="Nome do responsável" name='clientAccountableName' value={clientAccountableName} onChange={this.handleChange} />
                <Form.Input label="Endereço" name='clientAddress' value={clientAddress} onChange={this.handleChange} />
                <Form.Input label="Cidade" name='clientCity' value={clientCity} onChange={this.handleChange} />
                <Form.Input label="Telefone" name='clientTel' value={clientTel} onChange={this.handleChange} />
                <Form.Input id="client-img" label="Logo do cliente" type="file" accept=".png,.jpg,.jpeg" />
              </Form>
            </Segment>
          </Grid.Column>
          <Grid.Column>
            <Segment>
              <Divider horizontal>{Strings.EXECUTING_COMPANY_INFO}</Divider>
              <Form>
                <Form.Input label="Nome da empresa" name='executingCompanyName' value={executingCompanyName} onChange={this.handleChange} />
                <Form.Input label="Nome do responsável" name='executingAccountableName' value={executingAccountableName} onChange={this.handleChange} />
                <Form.Input label="Telefone" name='executingTel' value={executingTel} onChange={this.handleChange} />
                <Form.Input label="E-mail" name='executingEmail' value={executingEmail} onChange={this.handleChange} />
                <Form.Input id="executor-img" label="Logo da empresa executora" type="file" accept=".png,.jpg,.jpeg" />
              </Form>
            </Segment>

            <Grid columns={2} doubling>
              <Grid.Column verticalAlign="middle">
                <Segment>
                  <Divider horizontal>{Strings.CONFORMITY}</Divider>
                  <Dropdown
                    fluid
                    selection
                    placeholder={Strings.DROPDOWN_PLACEHOLDER}
                    options={dropdownOptions}
                    onChange={this.handleConformityDropdownItemClick} />
                </Segment>
              </Grid.Column>

              <Grid.Column>
                <Segment>
                  <Divider horizontal>Canais inclusos no relatório</Divider>

                  <Form>
                    <Form.Field style={{ marginBottom: "-1vh", textAlign: "center" }}>
                      <Checkbox
                        label={Strings.CHANNEL_1}
                        onChange={this.toggleChannel1Checkbox}
                        checked={this.state.isChannel1Checked} />
                    </Form.Field>
                    <Form.Field style={{ marginBottom: "-1vh", textAlign: "center" }}>
                      <Checkbox
                        label={Strings.CHANNEL_2}
                        onChange={this.toggleChannel2Checkbox}
                        checked={this.state.isChannel2Checked} />
                    </Form.Field>
                    <Form.Field style={{ marginBottom: "-1vh", marginLeft: "center" }}>
                      <Checkbox
                        label={Strings.CHANNEL_3}
                        onChange={this.toggleChannel3Checkbox}
                        checked={this.state.isChannel3Checked} />
                    </Form.Field>
                    <Form.Field style={{ marginBottom: "-1vh", marginLeft: "center" }}>
                      <Checkbox
                        label={Strings.CHANNEL_4}
                        onChange={this.toggleChannel4Checkbox}
                        checked={this.state.isChannel4Checked} />
                    </Form.Field>
                  </Form>

                </Segment>

              </Grid.Column>
            </Grid>

            <Segment>
              <Divider horizontal>{Strings.ANNOTATIONS}</Divider>
              <Form>
                <Form.Input type="text" name='notes' value={notes} onChange={this.handleChange} />
              </Form>
            </Segment>
          </Grid.Column>
        </Grid>

        <Button
          style={buttonStyle}
          color="teal"
          onClick={this.handleSubmit}>
          {Strings.GENERATE_REPORT}
        </Button>

        <div id="chart" style={chartsStyle}></div>
        <div id="barchart" style={chartsStyle}></div>

        <Dimmer page active={this.state.isGenerateReportDimmerActive} >
          <Loader size="big">{Strings.GENERATING_REPORT_WAITING}</Loader>
        </Dimmer>
      </div>
    );
  }
}

const mapStateToProps = state => ({
  channel: state.activeMenuItem.channel
});

const chartsStyle = {
  width: "1026px",
  height: "580px",
  display: "none",
  visibility: "hidden"
};

const divStyle = {
  margin: "2vh 2vw",
  textAlign: "center"
};

const buttonStyle = {
  margin: "2vh auto"
};

export default connect(mapStateToProps)(withRouter(Report));
